import { useEffect } from "react";
import { faTag } from "@fortawesome/pro-thin-svg-icons";

import Icon from "common/components/Icon";
import { WithStringValues } from "common/utils/types";
import { useListSearchState } from "common/hooks";

import Page from "client/common/components/layouts/Page";
import Body from "client/common/components/layouts/Body";
import Container from "client/common/components/layouts/Container";
import Results from "client/common/components/results/Results";
import { ProductListSource } from "client/analytics/types";
import { Paths } from "client/routes";

import { useFilterPanel, useProductDealsSearchQuery } from "../hooks";
import { PRODUCT_FILTER_DEFINITIONS, DEFAULT_PAGE_SIZE } from "../constants/filterDefinitions";
import { getProductFiltersFromSearch } from "../utils";
import { ProductDealsSearch, ProductSearchFilters, SelectedProductFilters } from "../types";
import ProductCardList from "./ProductCardList";
import ProductCardListHeader from "./ProductCardListHeader";
import FilterLayout from "./FilterLayout";
import FilterPanel from "./FilterPanel";

import styles from "../styles/DealsPage.module.less";

const initialSearch = {
  category_ids: [],
  manufacturer_ids: [],
  page: 1,
  page_size: DEFAULT_PAGE_SIZE,
  prescription_required: [],
  sort: "",
  species: [],
};

type DealsResultsProps = {
  search: WithStringValues<ProductDealsSearch>;
  onUpdateSearch: (search: Partial<ProductDealsSearch>) => void;
};

const DealsResults = ({ search, onUpdateSearch }: DealsResultsProps) => {
  const { data: products, isLoading, error, isRefetching } = useProductDealsSearchQuery(search);

  const filters = getProductFiltersFromSearch({ ...search, q: "" });

  const panel = useFilterPanel<SelectedProductFilters, ProductSearchFilters>({
    definitions: PRODUCT_FILTER_DEFINITIONS,
    response: products,
    filters,
    onChange: onUpdateSearch,
  });
  const { filterOptions, showFilterPanel, hasSelectedFilters } = panel;

  if (isLoading) {
    return <Results.Loading description="Loading deals" />;
  }

  if (error || !products) {
    return <Results.FetchError error={error} entityName="deal" />;
  }

  const emptyDescription = hasSelectedFilters
    ? "No deals match the filters you have applied."
    : "Deals change often. Check back soon for new eligible savings.";

  return (
    <FilterLayout panel={showFilterPanel && <FilterPanel.Rail {...panel} />}>
      <div className={styles.container}>
        <ProductCardListHeader
          paginator={products.paginator}
          isRefetching={isRefetching}
          entityName="deal"
          filterOptions={filterOptions}
          filters={filters}
          onChange={onUpdateSearch}
          showFilters={!showFilterPanel}
        />
        <FilterPanel.AppliedRow {...panel} />
        {products.results.length ? (
          <ProductCardList
            results={products.results}
            listSource={ProductListSource.DealsPage}
            listContext={{ numResults: products.paginator.count }}
            paginator={products.paginator}
            onPageChange={(page) => onUpdateSearch({ page })}
            entityName="deal"
            isNarrow={showFilterPanel}
          />
        ) : (
          <Results.None header="No deals found" description={emptyDescription} />
        )}
      </div>
    </FilterLayout>
  );
};

const DealsPage = () => {
  const [search, updateSearch] = useListSearchState<Paths["products"]["deals"]>(initialSearch);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [search.page]);

  return (
    <Page className={styles.page}>
      <div>
        <Container>
          <div className={styles.top}>
            <div className={styles.heading}>
              <h1 className={styles.title}>Deals</h1>
              <div className={styles.description}>
                Save on your pet&apos;s care with autoship and manufacturer discounts, plus
                limited-time offers, all in one place.
              </div>
            </div>
            <Icon className={styles.icon} icon={faTag} />
          </div>
        </Container>
        <div className={styles.main}>
          <Body>
            <DealsResults search={search} onUpdateSearch={updateSearch} />
          </Body>
        </div>
      </div>
    </Page>
  );
};

export default DealsPage;
